/**
 * IDS v2 icon resolution.
 *
 * The Contentstack content still references icons by their **IDS v1** names
 * (Portuguese, e.g. `celular_outline`), while the IDS v2 web component fetches
 * SVGs by their **v2** catalog name (English, e.g. `mobile`) from the IDS CDN.
 * Requesting a v1 name from the v2 CDN returns 403/404 and the icon never
 * renders.
 *
 * To make icon loading deterministic and CDN-independent (no 403), the IDS v2
 * icon set is vendored under `public/assets/ids2.0/icons/<name>.svg` and served
 * from the app's own origin. This module maps any incoming name to a known v2
 * catalog entry and returns the local URL.
 *
 * NOTE: the v1 -> v2 aliases below were derived from the IDS v2 catalog and
 * must be validated by the Design System team before go-live (see PR notes).
 */

const BASE = import.meta.env.VITE_BASE ?? '/';

/** Directory (relative to the app base) where the IDS v2 SVGs are hosted. */
export const IDS_ICON_BASE = `${BASE}assets/ids2.0/icons`;

/** Fallback icon used when an incoming name cannot be resolved. */
export const FALLBACK_ICON = 'external_link';

/** Names available in the vendored IDS v2 catalog (public/assets/ids2.0/icons). */
export const IDS_V2_ICONS: ReadonlySet<string> = new Set([
  'add_circle', 'arrow_down', 'arrow_left', 'arrow_order', 'arrow_order_down',
  'arrow_order_up', 'arrow_right', 'arrow_up', 'bank_cash_flow', 'bar_code',
  'bar_code_reader', 'calendar_day', 'calendar_month', 'card', 'card_digital',
  'card_group', 'cash_box', 'chat_dots', 'chat_message', 'check', 'check_circle',
  'check_small', 'checkbox', 'checkbox_unchecked', 'circle_filled', 'clock',
  'close', 'coin', 'company', 'configuration', 'copy', 'credit', 'discount',
  'doc', 'doc_folder', 'doc_invoice', 'dots_horizontal', 'download', 'edit',
  'error_circle', 'exchange', 'exit', 'explore', 'external_link',
  'extra_protection', 'eye_closed', 'eye_open', 'filter', 'food',
  'fraud_prevention', 'heart', 'help', 'home', 'ia', 'information', 'innovation',
  'installment', 'investment_stocks', 'investments', 'itoken_app', 'limit_bar',
  'list', 'loan', 'lock', 'menu', 'microphone', 'mobile', 'money', 'notification',
  'open_finance', 'payment_terminal', 'phone', 'pix', 'plus', 'protected_mode',
  'radio_button', 'radio_button_checked', 'receipt', 'reload', 'rewards', 'search',
  'share', 'shopping_bag', 'squares', 'star', 'target', 'thumbs_down', 'thumbs_up',
  'transaction', 'transport_car', 'transport_flight', 'trash', 'user', 'user_deal',
  'user_executive', 'user_id', 'video_play', 'wallet', 'warning',
]);

/**
 * Maps legacy IDS v1 icon names (as authored in Contentstack) to their IDS v2
 * catalog equivalent. Entries are grouped by correspondence type:
 *   1. direct  - same glyph kept under the same name in v2;
 *   2. renamed - glyph exists in v2 under a different name;
 *   3. no v2 match - v1 glyph removed; the nearest semantic v2 icon (reviewed and
 *      approved by the Design System team) is used. Each such entry documents the
 *      removed v1 glyph and any weaker/alternative options inline.
 */
export const ICON_ALIASES: Readonly<Record<string, string>> = {
  // --- Correspondência direta no v2 (mesmo glifo, nome mantido) ---
  acoes: 'investment_stocks',
  ajuda_outline: 'help',
  beneficios_outline: 'discount', // v1 `discount` glyph kept under same name in v2 (was wrongly aliased to `rewards`)
  calendario_outline: 'calendar_month',
  cartao_virtual: 'card_digital',
  cartoes_outline: 'card_group',
  celular_outline: 'mobile',
  documento_outline: 'doc',
  investimento_outline: 'investments',
  link_externo_outline: 'external_link',
  mais: 'plus',
  saldo_outline: 'money', // v1 `money` glyph kept under same name in v2 (was wrongly aliased to `wallet`, the v1 "conta corrente" glyph)
  usuario_outline: 'user',
  alimentacao_outline: 'food',
  aviso_outline: 'warning',
  busca_outline: 'search',
  compras_outline: 'shopping_bag',
  configuracao_outline: 'configuration',
  copiar_outline: 'copy',
  credito_outline: 'credit',
  documentos_pasta_outline: 'doc_folder',
  emprestimo_outline: 'loan',
  empresas_outline: 'company',
  fatura_outline: 'doc_invoice',
  favorito_outline: 'star',
  feedback_outline: 'chat_dots',
  gerente_outline: 'user_executive',
  home_outline: 'home',
  informacao_outline: 'information',
  inovacao_outline: 'innovation',
  itoken_outline: 'itoken_app',
  notificacao_outline: 'notification',
  pagamentos: 'bar_code_reader', // v1 `pagamentos` -> `bar_code_reader` (leitor) conforme catálogo v1
  parcelamento_outline: 'installment',
  pix_outline: 'pix',
  relogio_outline: 'clock',
  seguranca_outline: 'lock',
  sms_chat_outline: 'chat_message',
  telefone_outline: 'phone',
  timeline_outline: 'list',
  transferencia: 'transaction',
  viagem_outline: 'transport_flight',
  video_outline: 'video_play',

  // --- Glifo renomeado no v2 (existe, com outro nome) ---
  cambio_outline: 'exchange', // v1 sem ligature PT; conceitual, `exchange` é o termo oficial no v2
  curtir_outline: 'thumbs_up', // aproximação: ícone diferente do v1 `like` (não é rename oficial 1:1)
  download_outline: 'download', // rename exato (v1 `arrow_circle_down`)
  maquina_cartao_outline: 'payment_terminal', // oficial (v1 `payment_terminal_card`, alias `maquina_pos`)
  olho_ativo_outline: 'eye_open', // oficial (v1 `visibility_on`)
  openbank_outline: 'open_finance', // oficial (v1 `open_finance`)
  vida_outline: 'heart', // aproximação: `insurance_life` foi removido; não é rename oficial 1:1

  // --- Sem correspondência no v2: ícone mais próximo aprovado pelo time (de/para refinado) ---
  agencia_outline: 'company', // v1 `place` removido; sem equiv oficial
  agencia_personnalite_outline: 'company', // v1 `place_favorite` removido; sem equiv oficial
  aplicativos_outline: 'squares', // v1 `mobile_app` removido; `squares` (grade, alias `menu`)
  atendimento_outline: 'chat_message', // v1 `chat_information` removido; alt: `chat_dots`
  bike: 'transport_car', // v1 `transport_bike` removido; restaram apenas car/flight
  carrinho_outline: 'shopping_bag', // v1 `shopping_cart` removido
  cinema_outline: 'video_play', // v1 `cinema` removido
  compra_outline: 'shopping_bag', // v1 `arrow_circle_right` removido
  consorcio_auto_outline: 'money', // v1 `consortium_auto` removido; oficial: money
  consorcio_imobiliario_outline: 'money', // v1 `consortium_home` removido; oficial: money
  consorcio_outline: 'money', // v1 `consortium_home` removido; oficial: money
  contatos_outline: 'user', // v1 `contact_book` removido; alt: user_id/phone
  credito_auto_outline: 'credit', // v1 `credit_auto` removido; alt: transport_car
  credito_consignado_outline: 'credit', // v1 `credit_consigned` removido
  credito_imobiliario_outline: 'credit', // v1 `credit_home` removido; alt: home
  csv_outline: 'doc', // v1 `doc_csv` removido; docs por extensão removidos
  cultura_outline: 'discount', // v1 `ticket` removido; ideal: url própria
  desafios_outline: 'target', // v1 `trophy` removido; alt: rewards
  enviar_outline: 'share', // v1 `send` removido; sem equiv oficial
  globo_outline: 'explore', // v1 `earth` removido; alt: open_finance
  imposto_de_renda: 'doc_invoice', // v1 `ir` removido; alt: receipt
  inss: 'money', // v1 `inss` removido; ideal: url própria; alt: user_id
  jornal_outline: 'doc', // v1 `newspaper` removido; alt: list
  loja_outline: 'shopping_bag', // v1 `store` removido; sem equiv oficial
  pdf_outline: 'doc', // v1 `doc_pdf` removido; docs por extensão removidos
  pet_outline: 'heart', // v1 `pet` removido; aproximação fraca; ideal: url própria
  portabilidade_outline: 'exchange', // v1 `portability` removido
  poupanca_outline: 'cash_box', // v1 `savings` removido; alt: coin/money
  premio_outline: 'rewards', // v1 `medal` removido; medals removidos
  presente_outline: 'rewards', // v1 `gift` removido; sem equiv oficial
  produtos_outline: 'squares', // v1 `menu_squares` removido; oficial confirma squares
  qrcode: 'bar_code', // v1 `qr_code` removido; sem equiv oficial; alt: bar_code_reader
  relatorios_financeiros_outline: 'doc_invoice', // v1 `doc_finance` removido; alt: receipt/doc
  seguro: 'extra_protection', // v1 `insurance` removido (categoria insurance saiu)
  sinais_outline: 'help', // v1 `sign_language` removido; aproximação fraca; ideal: url própria
  sorriso_outline: 'thumbs_up', // v1 `smile` removido; alt: heart
  suporte_outline: 'help', // v1 `tool` removido
  tablet_outline: 'mobile', // v1 `tablet` removido; sem equiv oficial
  teatro_outline: 'star', // v1 `theater` removido; aproximação fraca; ideal: url própria
  'trevo-pic_outline': 'rewards', // v1 `clover` removido; aproximação; ideal: url própria; alt: star
  tv: 'video_play', // v1 `television` removido
  txt_outline: 'doc', // v1 `doc_txt` removido; docs por extensão removidos
  universitarios_outline: 'user', // v1 `education` removido; alt: user_id
  upload_outline: 'arrow_up', // v1 `arrow_circle_up` removido; alt: share
  word_outline: 'doc', // v1 `doc_word` removido; docs por extensão removidos
  previdencia_outline: 'investments', // v1 `vacation` removido; ideal: url própria (guarda-sol); alt: clock
};

/**
 * Resolves an incoming icon name (v1 or v2) to a known IDS v2 catalog name,
 * falling back to {@link FALLBACK_ICON} when no match exists.
 */
export function resolveIconName(name: string | undefined | null): string {
  const key = (name ?? '').trim();
  if (!key) return FALLBACK_ICON;
  if (IDS_V2_ICONS.has(key)) return key;
  const alias = ICON_ALIASES[key];
  if (alias && IDS_V2_ICONS.has(alias)) return alias;
  return FALLBACK_ICON;
}

/**
 * Returns the self-hosted URL for an icon name, ready to be passed to the IDS
 * `<idsw-icon url="...">` attribute (renders the SVG locally, never the CDN).
 */
export function resolveIconUrl(name: string | undefined | null): string {
  return `${IDS_ICON_BASE}/${resolveIconName(name)}.svg`;
}
